import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface DonationPackage {
  id: number;
  name: string;
  amount: number;
  silks_amount: number;
  vip_points: number;
  promotion_id: number | null;
  display_order: number;
  isactive: number;
}

export interface DonationMethod {
  id: number;
  method_name: string;
  method_key: string;
  isactive: number;
  config_json: string | null;
}

export interface DonationPromotion {
  id: number;
  name: string;
  description: string | null;
  type: 'percentage' | 'flat';
  value: number;
  method_id: number | null;
}

export interface DonationPackagesResponse {
  packages: DonationPackage[];
  methods: DonationMethod[];
  silk_rate: number;
  vip_points_rate: number;
  max_daily_limit: number;
}

export interface CalculateResponse {
  base_silks: number;
  bonus_silks: number;
  total_silks: number;
  promotion: DonationPromotion | null;
}

export interface CreatePaymentResponse {
  donation_id: number;
  method: string;
  approval_url?: string;
  order_id?: string;
  client_secret?: string;
  publishable_key?: string;
}

export interface DonationStatusResponse {
  status: string;
  donation: {
    id: number;
    amount: number;
    silks_amount: number;
    status: string;
  };
}

async function fetchPackages(): Promise<DonationPackagesResponse> {
  const { data } = await api.get<{ status: string; data: DonationPackagesResponse }>(
    '/api/v1/donations/packages',
  );
  return data.data;
}

export function useDonationPackages() {
  return useQuery({
    queryKey: ['donations', 'packages'],
    queryFn: fetchPackages,
    staleTime: 300_000,
  });
}

export function useDonationCalculate() {
  return useMutation({
    mutationFn: async (payload: { amount: number; method_id?: number | null }) => {
      const { data } = await api.post<{ status: string; data: CalculateResponse }>(
        '/api/v1/donations/calculate',
        payload,
      );
      return data.data;
    },
  });
}

export function useCreateDonationPayment() {
  return useMutation({
    mutationFn: async (payload: { amount: number; method_id: number; silks_amount: number }) => {
      const { data } = await api.post<{ status: string; data: CreatePaymentResponse }>(
        '/api/v1/donations/payment',
        payload,
      );
      return data.data;
    },
  });
}

export function useDonationStatus(donationId: number | null, enabled = false) {
  return useQuery({
    queryKey: ['donations', donationId, 'status'],
    queryFn: async () => {
      const { data } = await api.get<{ status: string; data: DonationStatusResponse }>(
        `/api/v1/donations/${donationId}/status`,
      );
      return data.data;
    },
    enabled: enabled && donationId != null && donationId > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        return false;
      }
      return 5000;
    },
  });
}

export function useDonationHistory(page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['donations', 'history', page],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ data: { items: Array<{
        id: number; amount: number; silks_amount: number; status: string; created_at: string; method_name: string | null;
      }>; hasMore: boolean } }>('/api/v1/donations/history', { params: { page } });
      return data.data;
    },
  });
}
